import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";

import useCanReviewSellerForecast from "../../../../../hooks/useCanReviewSellerForecast";
import {
  refreshSellerForecastReviewsLocally,
  storeAnalyticsSelector,
  submitSellerForecastReview,
} from "../../../../../redux/slices/store_analytics";

import {
  FORECAST_EDIT_CUTOFF_DAY,
  getForecastEditWindow,
  getLatestForecastDate,
} from "./forecastEditWindow";

const StyledTableRow = styled(TableRow)(
  ({ theme, isLastInGroup, reviewStatus, isExpanded, isPrevExpanded }) => {
    let backgroundColor = "#ffffff";
    let borderLeft = "3px solid transparent";

    if (reviewStatus === "accepted") {
      backgroundColor = "#f0fdf4"; // Modern light green
      borderLeft = "3px solid #10b981";
    } else if (reviewStatus === "Override") {
      backgroundColor = "#eff6ff"; // Modern light blue
      borderLeft = "3px solid #3b82f6";
    }

    return {
      backgroundColor,
      borderLeft,
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor:
          reviewStatus === "accepted"
            ? "#dcfce7"
            : reviewStatus === "Override"
            ? "#dbeafe"
            : "#f8fafc",
        transform: "translateX(2px)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      },
      "& .MuiTableCell-root": {
        borderBottom: isExpanded
          ? "none"
          : isLastInGroup
          ? `1px solid ${theme.palette.divider}`
          : "none",
        borderTop: isPrevExpanded
          ? `1px solid ${theme.palette.divider}`
          : "none",
        padding: "16px 12px",
      },
    };
  }
);

const StyledHeaderCell = styled(TableCell)(() => ({
  fontWeight: 600,
  fontSize: "12px",
  backgroundColor: "#f8fafc",
  color: "#475569",
  position: "sticky",
  top: 0,
  zIndex: 10,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  padding: "16px 12px",
  borderBottom: "2px solid #e2e8f0",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
}));

const REASON_OPTIONS = [
  "Expected stockout in complementary SKU",
  "Launching new variation",
  "Discontinuing product",
  "Seasonal spike not captured by model",
  "Rank achieving efforts",
  "Other",
];

const isValidOverrideValue = (originalValue, overrideValue) => {
  const original = Number(originalValue);
  const override = Number(overrideValue);

  if (isNaN(original) || isNaN(override)) return true;
  if (original === override) return false;

  // Always allow 0 as a valid override value
  if (override === 0) return true;

  const minVal = Math.min(original, override);
  const maxVal = Math.max(original, override);
  const ratio = (minVal + 100) / (maxVal + 100);
  return ratio <= 0.85;
};

const getMinimumValidChange = (originalValue) => {
  const original = Number(originalValue);
  if (isNaN(original)) return null;

  // Calculate minimum valid values based on the formula:
  // (min + 100) / (max + 100) <= 0.85

  // For decrease: V = 0.85 * O - 15
  const minDecrease = Math.floor(0.85 * original - 15);

  // For increase: V = (O + 15) / 0.85
  const minIncrease = Math.ceil((original + 15) / 0.85);

  return {
    decrease: Math.max(0, minDecrease),
    increase: minIncrease,
  };
};

// Model value a review was made against. The API returns
// { total, original_breakdown_by_months: { month: { value } } }; reviews
// added locally after a submit keep the flat { month: value } we POST.
const getOriginalBreakdownValue = (review, month) => {
  const breakdown = review?.original_breakdown;
  return breakdown?.original_breakdown_by_months
    ? breakdown.original_breakdown_by_months[month]?.value
    : breakdown?.[month];
};

// Value shown for `month` in a row built from a seller review: the accepted
// model breakdown or the seller's override, coloured by review type.
const getReviewCell = (review, month) => {
  if (review?.review === "accepted") {
    return {
      value: getOriginalBreakdownValue(review, month) ?? "-",
      bg: "#dcfce7",
      border: "#86efac",
    };
  }
  if (review?.review === "override") {
    const monthsData =
      review.override?.override_by_months || review.override?.overrideByMonths;
    return {
      value:
        monthsData?.[month]?.value ?? review.override_breakdown?.[month] ?? "-",
      bg: "#dbeafe",
      border: "#93c5fd",
    };
  }
  return { value: "-" };
};

// Most recent review for `asin`, whichever forecast it was for
const findLatestReview = (reviews, asin) =>
  (reviews || [])
    .filter((review) => review.asin === asin)
    .reduce(
      (latest, review) =>
        !latest ||
        (review.review_date || "") > (latest.review_date || "") ||
        ((review.review_date || "") === (latest.review_date || "") &&
          (review.forecast_date || "") > (latest.forecast_date || ""))
          ? review
          : latest,
      null
    );

// MODEL / MANUAL tag shown next to a row built from a seller review
const ReviewSourceBadge = ({ review }) =>
  review?.review === "accepted" || review?.review === "override" ? (
    <Box
      component="span"
      sx={{
        backgroundColor: review.review === "accepted" ? "#dcfce7" : "#dbeafe",
        color: review.review === "accepted" ? "#15803d" : "#1d4ed8",
        fontWeight: 600,
        fontSize: "10px",
        letterSpacing: "0.5px",
        borderRadius: "12px",
        px: 1.5,
        py: 0.25,
      }}
    >
      {review.review === "accepted" ? "MODEL" : "MANUAL"}
    </Box>
  ) : null;

// Label cell at the start of each expanded-panel row. Rows above "Last Year
// Sales" draw a line under their label to separate the row headers.
const rowLabelSx = (separated) => ({
  width: "100px",
  minWidth: "100px",
  pr: 1,
  alignSelf: "stretch",
  display: "flex",
  alignItems: "center",
  ...(separated && { borderBottom: "1px solid #e2e8f0" }),
});

// Width of the trailing column holding each row's action (Save, Accept, badge)
const ACTION_COL_WIDTH = "110px";

const ActionCell = ({ children }) => (
  <Box
    sx={{
      width: ACTION_COL_WIDTH,
      minWidth: ACTION_COL_WIDTH,
      pl: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

// Local date as YYYY-MM-DD (toISOString would shift it to UTC)
const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
};

// Read-only row of the expanded forecast table: label, previous-month cell,
// then one cell per forecast month and an optional trailing `action`.
// `getCell(month)` returns { value, bg, border }. `striped` renders the
// hatched, dashed style used for the previous cycle.
const ReadOnlyForecastRow = ({
  label,
  months,
  previousMonthKey,
  getCell,
  action,
  striped,
  sx,
}) => {
  const renderValue = (value, bg) => (
    <Typography
      component="span"
      sx={{
        fontSize: "11px",
        fontWeight: 500,
        color: "#475569",
        ...(striped &&
          bg && {
            backgroundColor: bg,
            px: 0.75,
            py: 0.125,
            borderRadius: "3px",
          }),
      }}
    >
      {value}
    </Typography>
  );
  const cellBackground = (bg) =>
    !bg
      ? "transparent"
      : striped
      ? `repeating-linear-gradient(135deg, ${bg} 0 6px, #ffffff 6px 9px)`
      : bg;
  const cellBorder = (border) =>
    striped && border ? `1px dashed ${border}` : "1px solid #e2e8f0";
  const prevCell = previousMonthKey ? getCell(previousMonthKey) : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
      <Box sx={rowLabelSx(true)}>
        <Typography
          sx={{ fontWeight: 500, color: "#64748b", fontSize: "11px" }}
        >
          {label}
        </Typography>
      </Box>
      {prevCell && (
        <Box
          sx={{
            width: "100px",
            minWidth: "100px",
            padding: "6px 4px",
            textAlign: "center",
            background: cellBackground(prevCell.bg),
            borderRight: cellBorder(prevCell.border),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderValue(prevCell.value, prevCell.bg)}
        </Box>
      )}
      <Table
        size="small"
        sx={{
          flex: 1,
          tableLayout: "fixed",
          borderCollapse: "separate",
          "& .MuiTableCell-root": {
            padding: "6px 4px",
            fontSize: "11px",
            textAlign: "center",
            borderBottom: "none",
            borderRight: "1px solid #e2e8f0",
            "&:last-child": {
              borderRight: "none",
            },
          },
        }}
      >
        <TableBody>
          <TableRow>
            {months.map((month) => {
              const { value, bg, border } = getCell(month);
              return (
                <TableCell
                  key={month}
                  sx={{
                    background: cellBackground(bg),
                    borderRight: cellBorder(border),
                  }}
                >
                  {renderValue(value, bg)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
      <ActionCell>{action}</ActionCell>
    </Box>
  );
};

const ForecastRow = React.memo(
  ({
    row,
    rowKey,
    index,
    status,
    overrideData,
    isExpanded,
    isPrevExpanded,
    isSubmitting,
    currentForecast,
    hasPrediction,
    noActiveInventory,
    // forecastDate,
    predictions,
    predictionData,
    lastYearBreakdown,
    lastMonthUnits,
    lastYearLastMonthUnits,
    latestReview,
    previousReview,
    userAllowedToRespond,
    editWindow,
    handleToggleExpand,
    handleSubmitReview,
  }) => {
    const [inputs, setInputs] = useState(() => Array(7).fill(""));
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [savedMessage, setSavedMessage] = useState(null);

    const {
      sortedPredictions,
      startMonth,
      endMonth,
      previousMonthLabel,
      previousMonthKey,
    } = useMemo(() => {
      // Always show a fixed 7-month window starting from the current month,
      // filling any month missing from `predictions` with a value of 0 —
      // some forecasts only carry the months still ahead of their
      // calculated_at date, so `predictions` can have fewer than 7 entries.
      const now = new Date();
      const sorted = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const monthKey = `${year}-${month}`;
        sorted.push([monthKey, predictions?.[monthKey] || { value: 0 }]);
      }

      const startMonthDate =
        sorted.length > 0
          ? new Date(sorted[0][0].split("-")[0], sorted[0][0].split("-")[1] - 1)
          : null;
      const endMonthDate =
        sorted.length > 0
          ? new Date(
              sorted[sorted.length - 1][0].split("-")[0],
              sorted[sorted.length - 1][0].split("-")[1] - 1
            )
          : null;

      // Calculate previous month label
      let prevMonthLabel = "";
      let prevMonthKey = "";
      if (startMonthDate) {
        const prevMonthDate = new Date(startMonthDate);
        prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
        const year = prevMonthDate.getFullYear();
        const month = String(prevMonthDate.getMonth() + 1).padStart(2, "0");

        const monthName = prevMonthDate.toLocaleString("en-US", {
          month: "short",
        });
        const shortYear = String(year).slice(-2);
        prevMonthLabel = `${monthName} ${month}/${shortYear}`;
        prevMonthKey = `${year}-${month}`;
      }

      return {
        sortedPredictions: sorted,
        startMonth: startMonthDate
          ? startMonthDate.toLocaleString("en-US", {
              month: "short",
              year: "numeric",
            })
          : "",
        endMonth: endMonthDate
          ? endMonthDate.toLocaleString("en-US", {
              month: "short",
              year: "numeric",
            })
          : "",
        previousMonthLabel: prevMonthLabel,
        previousMonthKey: prevMonthKey,
      };
    }, [predictions]);

    // Model value per displayed month (0 where the model has none), the
    // reference for "equals model" and the 15% minimum-change rule.
    const modelValues = sortedPredictions.map(
      ([, data]) => Number(data.value) || 0
    );
    const inputErrors = inputs.map(
      (value, i) =>
        value !== "" &&
        Number(value) !== modelValues[i] &&
        !isValidOverrideValue(modelValues[i], value)
    );
    const hasInputErrors = inputErrors.some(Boolean);
    const allFilled = inputs.every((value) => value !== "");
    const equalsModel =
      allFilled && inputs.every((value, i) => Number(value) === modelValues[i]);

    const editingLocked = editWindow.status !== "open";
    const lockedMessage = editingLocked
      ? editWindow.message
      : !userAllowedToRespond
      ? "Only the store owner can accept or override forecasts"
      : "";
    const canSave = !lockedMessage && allFilled && !hasInputErrors;
    const saveTooltip =
      lockedMessage ||
      (!allFilled
        ? "Fill every month to save"
        : hasInputErrors
        ? "Please fix the override value errors before saving"
        : "");
    const notesRequired = reason === "Other";
    const canConfirmOverride =
      !!reason && (!notesRequired || notes.trim() !== "");

    const handleInputChange = (i, rawValue) => {
      const value = rawValue.replace(/[^0-9]/g, "");
      setInputs((prev) => {
        const next = [...prev];
        next[i] = value;
        return next;
      });
      setOverrideOpen(false);
      setSavedMessage(null);
    };

    // Accept only copies the model into the inputs; Save submits it
    const handleAcceptModel = () => {
      setInputs(modelValues.map(String));
      setOverrideOpen(false);
      setSavedMessage(null);
    };

    const handleCancelOverride = () => {
      setOverrideOpen(false);
      setReason("");
      setNotes("");
    };

    const submit = async (reviewType) => {
      const values = {};
      sortedPredictions.forEach(([month], i) => {
        values[month] = Number(inputs[i]);
      });
      const saved = await handleSubmitReview(
        rowKey,
        reviewType,
        reviewType === "Override"
          ? { predictions: values, reason, notes: notes.trim() }
          : null,
        row,
        predictionData
      );
      if (!saved) return;

      const date = todayISO();
      setSavedMessage(
        reviewType === "Override"
          ? {
              text: `Saved ${date}. Effective prediction now uses your manual forecast (${reason}).`,
              color: "#1d4ed8",
            }
          : {
              text: `Saved ${date}. Effective prediction now uses the model forecast.`,
              color: "#15803d",
            }
      );
      setInputs(Array(7).fill(""));
      setOverrideOpen(false);
      setReason("");
      setNotes("");
    };

    // Unchanged model values are an acceptance; anything else needs a reason
    const handleSave = () => {
      if (equalsModel) {
        submit("accepted");
      } else {
        setOverrideOpen(true);
      }
    };

    if (isSubmitting) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
            <CircularProgress size={30} />
          </TableCell>
        </TableRow>
      );
    }

    return (
      <React.Fragment>
        <StyledTableRow
          isEvenLogicalRow={row.isEvenLogicalRow}
          isLastInGroup={row.isLastInGroup}
          reviewStatus={status}
          isExpanded={isExpanded}
          isPrevExpanded={isPrevExpanded}
          onClick={() => handleToggleExpand(rowKey)}
          sx={{ cursor: "pointer" }}
        >
          <TableCell>
            {/* The click bubbles to the row, which toggles the expansion */}
            <IconButton
              aria-label="expand row"
              size="small"
              sx={{
                color: "#64748b",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  color: "#3b82f6",
                  transform: "scale(1.1)",
                },
              }}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#64748b", fontSize: "13px" }}
            >
              {index + 1}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#1e293b", fontSize: "13px" }}
            >
              {row.store}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              {row.parent_asin}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              {row.child_asin}
            </Typography>
          </TableCell>
          <TableCell>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                  color: "#475569",
                  fontWeight: 500,
                }}
              >
                {row.sku}
              </Typography>
              {noActiveInventory && (
                <Tooltip title="Out of Stock" arrow placement="top">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#fff7ed",
                      color: "#c2410c",
                      fontWeight: 600,
                      fontSize: "10px",
                      borderRadius: "12px",
                      px: 1.5,
                      py: 0.25,
                      border: "1px solid #fed7aa",
                      width: "fit-content",
                    }}
                  >
                    OoS
                  </Box>
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell>
            {overrideData ? (
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: "line-through",
                    color: "#94a3b8",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {currentForecast}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#2563eb",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  {overrideData.total}
                </Typography>
              </Box>
            ) : !hasPrediction ? (
              <Tooltip
                title="No forecast data available for this SKU"
                arrow
                placement="top"
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    backgroundColor: "#f1f5f9",
                    color: "#94a3b8",
                    fontWeight: 600,
                    fontSize: "11px",
                    borderRadius: "16px",
                    px: 2,
                    py: 0.5,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  No forecast
                </Box>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e293b",
                }}
              >
                {currentForecast}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: overrideData
                  ? "#dbeafe"
                  : status === "accepted"
                  ? "#dcfce7"
                  : "#fef3c7",
                color: overrideData
                  ? "#1e40af"
                  : status === "accepted"
                  ? "#166534"
                  : "#92400e",
                fontWeight: 600,
                fontSize: "11px",
                borderRadius: "16px",
                px: 2.5,
                py: 0.75,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                border: overrideData
                  ? "1px solid #93c5fd"
                  : status === "accepted"
                  ? "1px solid #86efac"
                  : "1px solid #fde68a",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              {overrideData
                ? "Override"
                : status === "accepted"
                ? "Accepted"
                : "Pending"}
            </Box>
          </TableCell>
        </StyledTableRow>
        <TableRow style={{ borderBottom: "none" }}>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: "none",
            }}
            colSpan={8}
          >
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  margin: 2,
                  backgroundColor: "#f8fafc",
                  borderRadius: "12px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  component="div"
                  sx={{
                    fontSize: "13px",
                    color: "#1e293b",
                    mb: 2,
                  }}
                >
                  <span style={{ fontWeight: 500, color: "#475569" }}>
                    ML Forecast - Next 7 Months ({startMonth} - {endMonth}):{" "}
                  </span>
                  {overrideData ? (
                    <span style={{ color: "#2563eb", fontWeight: 600 }}>
                      <span
                        style={{
                          textDecoration: "line-through",
                          color: "#94a3b8",
                          marginRight: "8px",
                          fontWeight: 400,
                        }}
                      >
                        {currentForecast} units
                      </span>
                      <span>{overrideData.total} units</span>
                    </span>
                  ) : (
                    <span style={{ color: "#2563eb", fontWeight: 600 }}>
                      {currentForecast} units
                    </span>
                  )}
                </Typography>
                {savedMessage && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: savedMessage.color,
                      mb: 1.5,
                    }}
                  >
                    {savedMessage.text}
                  </Typography>
                )}
                {/* Unified Forecast & Sales Table */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {/* Header Row with months */}
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: "100px", minWidth: "100px" }} />
                    {/* Previous Month Header */}
                    {previousMonthLabel && (
                      <Box
                        sx={{
                          width: "100px",
                          minWidth: "100px",
                          padding: "6px 4px",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#64748b",
                            fontWeight: 500,
                          }}
                        >
                          {previousMonthLabel}
                        </Typography>
                      </Box>
                    )}
                    <Table
                      size="small"
                      sx={{
                        flex: 1,
                        tableLayout: "fixed",
                        backgroundColor: "transparent",
                        "& .MuiTableCell-root": {
                          padding: "6px 4px",
                          fontSize: "11px",
                          textAlign: "center",
                          borderBottom: "none",
                          color: "#64748b",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          {sortedPredictions.map(([month]) => {
                            const [y, m] = month.split("-");
                            const monthName = new Date(
                              parseInt(y),
                              parseInt(m) - 1
                            ).toLocaleString("en-US", {
                              month: "short",
                            });
                            const shortYear = y.slice(-2);
                            const monthLabel = `${monthName} ${m}/${shortYear}`;
                            return (
                              <TableCell key={month}>{monthLabel}</TableCell>
                            );
                          })}
                        </TableRow>
                      </TableBody>
                    </Table>
                    <ActionCell />
                  </Box>

                  {/* Seller Input Row (hidden while editing is locked) */}
                  {!editingLocked && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={rowLabelSx(true)}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#2563eb",
                            fontSize: "12px",
                          }}
                        >
                          Seller input
                        </Typography>
                      </Box>
                      {/* Previous Month Actual Sales */}
                      {previousMonthLabel && (
                        <Box
                          sx={{
                            width: "100px",
                            minWidth: "100px",
                            padding: "6px 4px",
                            textAlign: "center",
                            backgroundColor: "#ffffff",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "40px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#1e293b",
                            }}
                          >
                            Actual: {lastMonthUnits ?? "-"}
                          </Typography>
                        </Box>
                      )}
                      <Table
                        size="small"
                        aria-label="seller input"
                        sx={{
                          flex: 1,
                          tableLayout: "fixed",
                          backgroundColor: "#ffffff",
                          borderRadius: "6px 6px 0 0",
                          overflow: "hidden",
                          borderCollapse: "separate",
                          "& .MuiTableCell-root": {
                            padding: "6px 4px",
                            fontSize: "13px",
                            textAlign: "center",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            "&:last-child": {
                              borderRight: "none",
                            },
                          },
                        }}
                      >
                        <TableBody>
                          <TableRow>
                            {sortedPredictions.map(([month], i) => {
                              const value = inputs[i];
                              const hasOverrideError = inputErrors[i];
                              const minValidChange = hasOverrideError
                                ? getMinimumValidChange(modelValues[i])
                                : null;

                              const tooltipMessage =
                                lockedMessage ||
                                (hasOverrideError && minValidChange
                                  ? `Value is too similar to original prediction (${
                                      modelValues[i]
                                    })\nAllowed value must be:${
                                      minValidChange.decrease > 0
                                        ? `\nlower than ${minValidChange.decrease} OR higher than ${minValidChange.increase}`
                                        : `\n0 OR higher than ${minValidChange.increase}`
                                    }`
                                  : "");

                              return (
                                <TableCell key={month}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Tooltip
                                      title={tooltipMessage}
                                      arrow
                                      placement="top"
                                      disableHoverListener={!tooltipMessage}
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            whiteSpace: "pre-line",
                                            textAlign: "center",
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <TextField
                                          size="small"
                                          disabled={!!lockedMessage}
                                          error={hasOverrideError}
                                          value={value}
                                          onChange={(e) =>
                                            handleInputChange(i, e.target.value)
                                          }
                                          inputProps={{
                                            inputMode: "numeric",
                                            style: {
                                              padding: "5px",
                                              textAlign: "center",
                                              fontSize: "12px",
                                              fontWeight: "600",
                                              color: hasOverrideError
                                                ? "#dc2626"
                                                : value !== "" &&
                                                  Number(value) !==
                                                    modelValues[i]
                                                ? "#2563eb"
                                                : "#1e293b",
                                            },
                                          }}
                                          sx={{
                                            width: "60px",
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "4px",
                                              backgroundColor: hasOverrideError
                                                ? "#fef2f2"
                                                : "#ffffff",
                                              transition:
                                                "all 0.2s ease-in-out",
                                              "&:hover": {
                                                backgroundColor:
                                                  hasOverrideError
                                                    ? "#fee2e2"
                                                    : "#f8fafc",
                                              },
                                              "&.Mui-focused": {
                                                backgroundColor:
                                                  hasOverrideError
                                                    ? "#fef2f2"
                                                    : "#ffffff",
                                                boxShadow: hasOverrideError
                                                  ? "0 0 0 3px rgba(220, 38, 38, 0.1)"
                                                  : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                                              },
                                              "& fieldset": {
                                                borderColor: hasOverrideError
                                                  ? "#ef4444"
                                                  : "#1e293b",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: hasOverrideError
                                                  ? "#dc2626"
                                                  : "#0f172a",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: hasOverrideError
                                                  ? "#ef4444"
                                                  : "#3b82f6",
                                                borderWidth: "2px",
                                              },
                                            },
                                          }}
                                        />
                                      </span>
                                    </Tooltip>
                                    <WarningIcon
                                      sx={{
                                        fontSize: "16px",
                                        color: "#dc2626",
                                        visibility: hasOverrideError
                                          ? "visible"
                                          : "hidden",
                                      }}
                                    />
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableBody>
                      </Table>
                      <ActionCell>
                        <Tooltip title={saveTooltip} arrow placement="top">
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleSave}
                              disabled={!canSave}
                              sx={{
                                backgroundColor: "#10b981",
                                color: "white",
                                textTransform: "none",
                                fontWeight: 600,
                                minWidth: "80px",
                                borderRadius: "8px",
                                fontSize: "13px",
                                boxShadow: "0 2px 4px rgba(16, 185, 129, 0.3)",
                                "&:hover": {
                                  backgroundColor: "#059669",
                                },
                                "&:disabled": {
                                  backgroundColor: "#e5e7eb",
                                  color: "#9ca3af",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              Save
                            </Button>
                          </span>
                        </Tooltip>
                      </ActionCell>
                    </Box>
                  )}

                  {/* Model Predictions Row */}
                  <ReadOnlyForecastRow
                    label={`Model predictions (${
                      predictionData?.calculated_at || "-"
                    })`}
                    months={sortedPredictions.map(([month]) => month)}
                    previousMonthKey={
                      previousMonthLabel ? previousMonthKey : ""
                    }
                    getCell={(month) => ({
                      value: predictions?.[month]?.value ?? "-",
                    })}
                    action={
                      !editingLocked && (
                        <Tooltip
                          title={
                            lockedMessage ||
                            (!hasPrediction
                              ? "No forecast data available for this SKU"
                              : "Copy the model forecast into Seller input")
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleAcceptModel}
                              disabled={!!lockedMessage || !hasPrediction}
                              sx={{
                                color: "#15803d",
                                borderColor: "#86efac",
                                textTransform: "none",
                                fontWeight: 600,
                                minWidth: "80px",
                                borderRadius: "8px",
                                fontSize: "13px",
                                "&:hover": {
                                  backgroundColor: "#ecfdf3",
                                  borderColor: "#15803d",
                                },
                              }}
                            >
                              Accept
                            </Button>
                          </span>
                        </Tooltip>
                      )
                    }
                    sx={{ mt: 1.7 }}
                  />
                </Box>

                {/* Effective Prediction Row */}
                <ReadOnlyForecastRow
                  label={`Effective prediction (${
                    latestReview?.review_date ||
                    latestReview?.forecast_date ||
                    "-"
                  })`}
                  months={sortedPredictions.map(([month]) => month)}
                  previousMonthKey={previousMonthLabel ? previousMonthKey : ""}
                  getCell={(month) => getReviewCell(latestReview, month)}
                  action={<ReviewSourceBadge review={latestReview} />}
                  sx={{ mt: 0.5 }}
                />

                {/* Previous Effective Prediction Row (previous_seller_reviews) */}
                <ReadOnlyForecastRow
                  label={`Previous effective prediction (${
                    previousReview?.review_date ||
                    previousReview?.forecast_date ||
                    "-"
                  })`}
                  months={sortedPredictions.map(([month]) => month)}
                  previousMonthKey={previousMonthLabel ? previousMonthKey : ""}
                  getCell={(month) => getReviewCell(previousReview, month)}
                  action={<ReviewSourceBadge review={previousReview} />}
                  striped
                  sx={{ mt: 0.5 }}
                />

                {/* Last Year Sales Row */}
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Box sx={rowLabelSx(false)}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: "#64748b",
                        fontSize: "11px",
                      }}
                    >
                      Last Year Sales
                    </Typography>
                  </Box>
                  {/* Previous Month Last Year Sales */}
                  {previousMonthLabel && (
                    <Box
                      sx={{
                        width: "100px",
                        minWidth: "100px",
                        padding: "6px 4px",
                        textAlign: "center",
                        backgroundColor: "#fefce8",
                        borderRight: "1px solid #fde047",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#78716c",
                        }}
                      >
                        {lastYearLastMonthUnits ?? "-"}
                      </Typography>
                    </Box>
                  )}
                  <Table
                    size="small"
                    sx={{
                      flex: 1,
                      tableLayout: "fixed",
                      backgroundColor: "#fefce8",
                      borderRadius: "0 0 6px 6px",
                      overflow: "hidden",
                      borderTop: "none",
                      borderCollapse: "separate",
                      "& .MuiTableCell-root": {
                        padding: "6px 4px",
                        fontSize: "11px",
                        textAlign: "center",
                        borderBottom: "none",
                        borderRight: "1px solid #fde047",
                        "&:last-child": {
                          borderRight: "none",
                        },
                      },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        {sortedPredictions.map(([month]) => {
                          const [year, monthNum] = month.split("-");
                          const lastYearMonth = `${
                            parseInt(year) - 1
                          }-${monthNum}`;
                          const lastYearValue =
                            lastYearBreakdown?.[lastYearMonth];

                          return (
                            <TableCell key={month}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#78716c",
                                  fontWeight: 500,
                                  fontSize: "11px",
                                }}
                              >
                                {lastYearValue !== undefined
                                  ? lastYearValue
                                  : "-"}
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                  <ActionCell />
                </Box>

                {/* Override Details: one reason and note for the whole override */}
                {overrideOpen && (
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mt: 2 }}
                  >
                    <Box
                      sx={{
                        width: "100px",
                        minWidth: "100px",
                        pr: 1,
                        pt: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#3b82f6",
                          fontSize: "11px",
                        }}
                      >
                        Override Details
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 2,
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        border: "1px solid #3b82f6",
                        boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)",
                      }}
                    >
                      <FormControl size="small" required sx={{ width: 300 }}>
                        <InputLabel
                          id={`reason-select-label-${rowKey}`}
                          sx={{ fontSize: "14px" }}
                        >
                          Reason
                        </InputLabel>
                        <Select
                          labelId={`reason-select-label-${rowKey}`}
                          value={reason}
                          label="Reason"
                          onChange={(e) => setReason(e.target.value)}
                          sx={{ fontSize: "13px", borderRadius: "6px" }}
                        >
                          {REASON_OPTIONS.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              sx={{ fontSize: "13px" }}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Notes"
                        required={notesRequired}
                        multiline
                        minRows={2}
                        size="small"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            fontSize: "13px",
                          },
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelOverride}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "8px",
                          color: "#475569",
                          borderColor: "#cbd5e1",
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => submit("Override")}
                        disabled={!canConfirmOverride}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "8px",
                          whiteSpace: "nowrap",
                          backgroundColor: "#3b82f6",
                          "&:hover": { backgroundColor: "#2563eb" },
                        }}
                      >
                        Save override
                      </Button>
                    </Box>
                    <ActionCell />
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
);

const getDefaultPredictionData = () => {
  const predictions = {};
  const now = new Date();

  // Generate 7 months starting from the current month
  for (let i = 0; i < 7; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const monthKey = `${year}-${month}`;
    predictions[monthKey] = { value: 0 };
  }

  return {
    predictions,
    forecast_total: 0,
    calculated_at: null,
  };
};

const SellerForecast = ({ store, marketplace }) => {
  const dispatch = useDispatch();

  const {
    activeInventory,
    activeInventoryLoading,
    predictedUnits,
    predictedUnitsLoading,
    sellerForecastReviews,
    previousSellerForecastReviews,
    pastUnitsSales,
  } = useSelector(storeAnalyticsSelector);

  const filteredPredictedUnits = useMemo(() => {
    if (!predictedUnits) return [];
    if (Array.isArray(predictedUnits)) return predictedUnits;
    if (typeof predictedUnits === "object")
      return Object.values(predictedUnits);
    return [];
  }, [predictedUnits]);

  const [reviewStatus, setReviewStatus] = useState({});
  const [overrides, setOverrides] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [submittingRows, setSubmittingRows] = useState({});
  const userAllowedToRespond = useCanReviewSellerForecast(store);

  // Month the model forecast on screen was issued in (its calculated_at is
  // always the 1st of that month, whatever day the rows landed).
  const latestForecastDate = useMemo(
    () => getLatestForecastDate(filteredPredictedUnits),
    [filteredPredictedUnits]
  );
  // Editing window: opens once this month's forecast has actually landed in
  // demand_forecast_results (often a day or more after the 1st) and closes
  // after the cut-off day. See forecastEditWindow.js. Memoised on the day so
  // the rows' React.memo keeps working.
  const today = new Date().toDateString();
  const editWindow = useMemo(
    () => getForecastEditWindow(latestForecastDate, new Date(today)),
    [latestForecastDate, today]
  );

  // Returns true once the review is saved, so the row can confirm it.
  // `overrideInput` is { predictions: { month: value }, reason, notes }.
  const handleSubmitReview = useCallback(
    async (
      rowKey,
      status,
      overrideInput = null,
      rowData = null,
      predictionData = null
    ) => {
      if (!rowData || !predictionData) return false;

      // Prepare original month breakdown
      const originalBreakdown = {};
      Object.entries(predictionData?.predictions || {}).forEach(
        ([key, data]) => {
          originalBreakdown[key] = Number(data.value || 0);
        }
      );

      const payload = {
        store: rowData.store,
        parent_asin: rowData.parent_asin,
        child_asin: rowData.child_asin,
        forecast_total: predictionData?.forecast_total || 0,
        forecast_date: predictionData?.calculated_at || "",
        review: status === "Override" ? "override" : "accepted",
        marketplace: marketplace,
        original_breakdown: originalBreakdown,
      };

      let overrideTotal = 0;
      if (status === "Override" && overrideInput) {
        // The backend stores reason/notes per month, so the single
        // reason and note are copied to every month
        const overrideByMonth = {};
        const months = new Set([
          ...Object.keys(originalBreakdown),
          ...Object.keys(overrideInput.predictions),
        ]);
        months.forEach((month) => {
          overrideByMonth[month] = {
            value: overrideInput.predictions[month] ?? originalBreakdown[month],
            reason: overrideInput.reason,
            notes: overrideInput.notes,
          };
        });

        overrideTotal = Object.values(overrideByMonth).reduce(
          (sum, monthData) => sum + monthData.value,
          0
        );

        payload.override = {
          total: overrideTotal,
          override_by_months: overrideByMonth,
        };
      }

      setSubmittingRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));

      try {
        // The thunk reports failures (and shows the error) instead of throwing
        const result = await dispatch(
          submitSellerForecastReview(payload, store, marketplace)
        );
        if (!result?.success) return false;

        dispatch(refreshSellerForecastReviewsLocally(payload));

        setReviewStatus((prev) => ({
          ...prev,
          [rowKey]: status,
        }));

        if (status === "Override" && overrideInput) {
          const override_reason = {};
          const override_notes = {};
          Object.keys(payload.override.override_by_months).forEach((month) => {
            override_reason[month] = overrideInput.reason;
            override_notes[month] = overrideInput.notes;
          });

          setOverrides((prev) => ({
            ...prev,
            [rowKey]: {
              total: overrideTotal,
              predictions: overrideInput.predictions,
              override_reason,
              override_notes,
            },
          }));
        } else {
          setOverrides((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
          });
        }
        return true;
      } catch (err) {
        console.error("Failed to save review", err);
        alert("Failed to save review");
        return false;
      } finally {
        setSubmittingRows((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
      }
    },
    [dispatch, store, marketplace]
  );

  // Review of the forecast currently shown for an ASIN, if any
  const findCurrentReview = useCallback(
    (childAsin, predictionData) =>
      sellerForecastReviews?.find((review) => {
        if (review.asin !== childAsin) return false;

        if (review.forecast_date === "1970-01-01") {
          const reviewMonth = review.review_date
            ? review.review_date.slice(0, 7)
            : null;
          const hasNewerForecast = filteredPredictedUnits.some(
            (unit) =>
              unit.calculated_at && unit.calculated_at.slice(0, 7) > reviewMonth
          );
          return !hasNewerForecast;
        }

        return (
          (review.forecast_date || null) ===
          (predictionData?.calculated_at || null)
        );
      }),
    [sellerForecastReviews, filteredPredictedUnits]
  );

  const handleToggleExpand = useCallback((rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  }, []);

  const groupedData = useMemo(() => {
    if (!activeInventory) return [];

    const rows = Object.values(activeInventory);
    const groups = {};

    rows.forEach((row, index) => {
      const groupKey = `${row.store}-${row.child_asin}`;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push({ ...row, originalIndex: index });
    });

    const result = [];
    let logicalRowIndex = 0;

    Object.values(groups).forEach((group) => {
      const isEvenLogicalRow = logicalRowIndex % 2 === 1;
      if (group.length > 0) {
        const skus = group
          .map((r) => r.sku)
          .filter(Boolean)
          .join(", ");
        const row = {
          ...group[0],
          sku: skus,
          originalGroup: group,
          logicalRowIndex,
          isEvenLogicalRow,
          isLastInGroup: true,
          groupSize: group.length,
        };
        result.push(row);
      }
      logicalRowIndex++;
    });

    // Include ASINs that have forecasts but are absent from active-inventory
    // (e.g. out-of-stock products the backend excluded). We recover the SKU
    // name and parent ASIN from pastUnitsSales so the row is identifiable.
    const coveredChildAsins = new Set(result.map((r) => r.child_asin));
    filteredPredictedUnits.forEach((unit) => {
      const childAsin = unit.child_asin || unit.asin;
      if (!childAsin || coveredChildAsins.has(childAsin)) return;

      // Use pastUnitsSales as a lookup for SKU / parent_asin.
      // Skip marketplace-variant entries (amzn.gr.*) — keep the canonical SKU.
      const primaryPastData = (pastUnitsSales || []).find(
        (s) => s.child_asin === childAsin && !s.sku?.startsWith("amzn.gr")
      );
      if (!primaryPastData) return; // No identifiable SKU — skip silently

      const baseRow = {
        store,
        parent_asin: primaryPastData.parent_asin || "",
        child_asin: childAsin,
        sku: primaryPastData.sku || childAsin,
        fba_inventory: 0,
        awd_inventory: 0,
        awd_inbound: 0,
        in_work_inventory: 0,
        on_the_way_inventory: 0,
        "3pl_inventory": 0,
        all_inventory: 0,
        incoming_shipments: [],
        noActiveInventory: true,
      };

      // Build originalGroup so pastUnitsSales lookups in the render work correctly
      const groupEntries = (pastUnitsSales || []).filter(
        (s) => s.child_asin === childAsin && !s.sku?.startsWith("amzn.gr")
      );
      const originalGroup = groupEntries.map((s) => ({
        ...baseRow,
        sku: s.sku,
      }));

      result.push({
        ...baseRow,
        originalGroup: originalGroup.length ? originalGroup : [baseRow],
        logicalRowIndex,
        isEvenLogicalRow: logicalRowIndex % 2 === 1,
        isLastInGroup: true,
        groupSize: 1,
      });
      coveredChildAsins.add(childAsin);
      logicalRowIndex++;
    });

    // Group siblings under their parent ASIN, by last month's actual sales
    // (the "Actual" cell): parents by the sum of their children, children by
    // their own, highest first. ASIN breaks ties so the order is stable.
    const lastMonthSales = (row) =>
      (row.originalGroup || [row]).reduce((sum, groupRow) => {
        const pastData = (pastUnitsSales || []).find(
          (unit) => unit.sku === groupRow.sku
        );
        return sum + (Number(pastData?.last_month_units) || 0);
      }, 0);

    const parents = {};
    result.forEach((row) => {
      const parentKey = row.parent_asin || "";
      if (!parents[parentKey]) {
        parents[parentKey] = { parentKey, total: 0, children: [] };
      }
      const sales = lastMonthSales(row);
      parents[parentKey].total += sales;
      parents[parentKey].children.push({ row, sales });
    });

    const bySalesThenAsin = (salesA, salesB, asinA, asinB) =>
      salesB - salesA || asinA.localeCompare(asinB);

    return Object.values(parents)
      .sort((a, b) =>
        bySalesThenAsin(a.total, b.total, a.parentKey, b.parentKey)
      )
      .flatMap((parent) =>
        parent.children
          .sort((a, b) =>
            bySalesThenAsin(
              a.sales,
              b.sales,
              a.row.child_asin || "",
              b.row.child_asin || ""
            )
          )
          .map(({ row }) => row)
      );
  }, [activeInventory, filteredPredictedUnits, pastUnitsSales, store]);

  const { csvData, csvHeaders } = useMemo(() => {
    if (!activeInventory || !sellerForecastReviews || !groupedData.length)
      return { csvData: [], csvHeaders: [] };

    const filteredRows = groupedData;

    // Collect all forecast month keys across rows.
    const allMonths = new Set();
    filteredRows.forEach((row) => {
      const predictionData =
        filteredPredictedUnits.find(
          (unit) => (unit.child_asin || unit.asin) === row.child_asin
        ) || getDefaultPredictionData();
      if (predictionData?.predictions) {
        Object.keys(predictionData.predictions).forEach((m) =>
          allMonths.add(m)
        );
      }
    });
    const sortedMonths = [...allMonths].sort((a, b) => a.localeCompare(b));

    // Previous month (one month before the first forecast month) - used to
    // surface the prev-review value for that month in the "Prev. Review" row.
    let csvPrevMonthKey = "";
    let csvPrevMonthLabel = "";
    if (sortedMonths.length > 0) {
      const [y, m] = sortedMonths[0].split("-");
      const prevDate = new Date(parseInt(y), parseInt(m) - 2);
      const prevYear = prevDate.getFullYear();
      const prevMonth = String(prevDate.getMonth() + 1).padStart(2, "0");
      csvPrevMonthKey = `${prevYear}-${prevMonth}`;
      const monthName = prevDate.toLocaleString("en-US", { month: "short" });
      const shortYear = String(prevYear).slice(-2);
      csvPrevMonthLabel = `${monthName} ${prevMonth}/${shortYear}`;
    }

    const monthLabels = sortedMonths.map((month) => {
      const [y, m] = month.split("-");
      const monthName = new Date(parseInt(y), parseInt(m) - 1).toLocaleString(
        "en-US",
        { month: "short" }
      );
      const shortYear = y.slice(-2);
      return `${monthName} ${m}/${shortYear}`;
    });

    const allMonthKeys = csvPrevMonthKey
      ? [csvPrevMonthKey, ...sortedMonths]
      : sortedMonths;
    const allMonthLabelsForKeys = csvPrevMonthKey
      ? [csvPrevMonthLabel, ...monthLabels]
      : monthLabels;

    const headers = [
      { label: "#", key: "#" },
      { label: "Store", key: "Store" },
      { label: "Parent ASIN", key: "Parent ASIN" },
      { label: "Child ASIN", key: "Child ASIN" },
      { label: "SKU", key: "SKU" },
      {
        label: "Sales Forecast Next 7 Months (day to day)",
        key: "Sales Forecast Next 7 Months (day to day)",
      },
      { label: "Status", key: "Status" },
      { label: "Type", key: "Type" },
      ...allMonthLabelsForKeys.map((label, i) => ({
        label,
        key: `month_${allMonthKeys[i]}`,
      })),
    ];

    const ROW_TYPES = [
      { key: "prediction", label: "Prediction" },
      { key: "model_original", label: "Model Original" },
      { key: "last_year", label: "Last Year" },
      { key: "effective_prediction", label: "Effective Prediction" },
      { key: "prev_review", label: "Prev. Review" },
      { key: "reason", label: "Reason" },
    ];

    const data = [];

    filteredRows.forEach((row, index) => {
      const rowKey = `${row.store}-${row.parent_asin}-${row.child_asin}`;
      const predictionData =
        filteredPredictedUnits.find(
          (unit) => (unit.child_asin || unit.asin) === row.child_asin
        ) || getDefaultPredictionData();
      const predictions = predictionData?.predictions || {};
      const currentForecast = predictionData?.forecast_total || 0;

      const existingReview = sellerForecastReviews.find((review) => {
        if (review.asin !== row.child_asin) return false;
        if (review.forecast_date === "1970-01-01") {
          const reviewMonth = review.review_date
            ? review.review_date.slice(0, 7)
            : null;
          const hasNewerForecast = filteredPredictedUnits.some(
            (unit) =>
              unit.calculated_at && unit.calculated_at.slice(0, 7) > reviewMonth
          );
          return !hasNewerForecast;
        }
        return (
          (review.forecast_date || null) ===
          (predictionData?.calculated_at || null)
        );
      });

      let overrideDataFinal = overrides[rowKey];
      if (
        !overrideDataFinal &&
        existingReview &&
        existingReview.review === "override"
      ) {
        if (existingReview.override) {
          const preds = {};
          const reasons = {};
          const notes = {};
          const monthsData =
            existingReview.override.override_by_months ||
            existingReview.override.overrideByMonths;
          if (monthsData) {
            Object.entries(monthsData).forEach(([month, d]) => {
              preds[month] = d.value;
              if (d.reason) reasons[month] = d.reason;
              if (d.notes) notes[month] = d.notes;
            });
          }
          overrideDataFinal = {
            total: existingReview.override.total,
            predictions: preds,
            override_reason: reasons,
            override_notes: notes,
          };
        } else {
          overrideDataFinal = {
            total: existingReview.override_total,
            predictions: existingReview.override_breakdown,
            override_reason: existingReview.override_reason,
            override_notes: existingReview.override_notes,
          };
        }
      }

      const statusFinal =
        reviewStatus[rowKey] ||
        (existingReview
          ? existingReview.review === "override"
            ? "Override"
            : "Accepted"
          : "Pending");

      const forecastTotal = overrideDataFinal
        ? overrideDataFinal.total
        : currentForecast;

      const lastYearBreakdown = {};
      if (row.originalGroup && pastUnitsSales) {
        row.originalGroup.forEach((groupRow) => {
          const pastData = pastUnitsSales.find(
            (unit) => unit.sku === groupRow.sku
          );
          if (pastData?.last_year_units_next_7m_breakdown) {
            Object.entries(pastData.last_year_units_next_7m_breakdown).forEach(
              ([month, value]) => {
                lastYearBreakdown[month] =
                  (lastYearBreakdown[month] || 0) + (Number(value) || 0);
              }
            );
          }
        });
      }

      const prevReview = previousSellerForecastReviews
        ? previousSellerForecastReviews.find((r) => r.asin === row.child_asin)
        : null;

      // Same review the "Effective prediction" row shows in the table.
      const latestReview = findLatestReview(
        sellerForecastReviews,
        row.child_asin
      );

      const valueByType = {
        prediction: (month) => {
          const overrideVal = overrideDataFinal?.predictions?.[month];
          const originalVal = predictions[month]?.value;
          if (overrideVal !== undefined) return overrideVal;
          return originalVal !== undefined ? originalVal : "";
        },
        model_original: (month) => {
          const originalVal = predictions[month]?.value;
          return originalVal !== undefined ? originalVal : "";
        },
        last_year: (month) => {
          const [year, monthNum] = month.split("-");
          const lastYearMonth = `${parseInt(year) - 1}-${monthNum}`;
          return lastYearBreakdown[lastYearMonth] !== undefined
            ? lastYearBreakdown[lastYearMonth]
            : "";
        },
        effective_prediction: (month) => {
          const { value } = getReviewCell(latestReview, month);
          return value === "-" ? "" : value;
        },
        prev_review: (month) => {
          if (!prevReview) return "";
          if (prevReview.review === "accepted") {
            const v = getOriginalBreakdownValue(prevReview, month);
            return v !== undefined ? v : "";
          }
          if (prevReview.review === "override") {
            const monthsData =
              prevReview.override?.override_by_months ||
              prevReview.override?.overrideByMonths;
            const v = monthsData?.[month]?.value;
            return v !== undefined ? v : "";
          }
          return "";
        },
        reason: (month) => {
          if (!overrideDataFinal) return "";
          const isOverriddenMonth =
            overrideDataFinal.predictions?.[month] !== undefined;
          if (!isOverriddenMonth) return "";

          const reasonRaw = overrideDataFinal.override_reason;
          const notesRaw = overrideDataFinal.override_notes;

          let reason = "";
          if (reasonRaw && typeof reasonRaw === "object") {
            reason = reasonRaw[month] || "";
          } else if (typeof reasonRaw === "string") {
            reason = reasonRaw;
          }

          let notes = "";
          if (notesRaw && typeof notesRaw === "object") {
            notes = notesRaw[month] || "";
          } else if (typeof notesRaw === "string") {
            notes = notesRaw;
          }

          if (!reason && !notes) return "";
          if (reason && notes) return `${reason} - ${notes}`;
          return reason || notes;
        },
      };

      ROW_TYPES.forEach((t, typeIndex) => {
        // Only the first row of each product carries the product-level fields;
        // the other type rows leave those columns blank to make the grouping
        // visually obvious in the CSV.
        const isFirstTypeRow = typeIndex === 0;
        const csvRow = {
          "#": isFirstTypeRow ? index + 1 : "",
          Store: isFirstTypeRow ? row.store : "",
          "Parent ASIN": isFirstTypeRow ? row.parent_asin : "",
          "Child ASIN": isFirstTypeRow ? row.child_asin : "",
          SKU: isFirstTypeRow ? row.sku : "",
          "Sales Forecast Next 7 Months (day to day)": isFirstTypeRow
            ? forecastTotal
            : "",
          Status: isFirstTypeRow ? statusFinal : "",
          Type: t.label,
        };
        allMonthKeys.forEach((month) => {
          // The previous month column is only meaningful for the review
          // rows (Effective Prediction, Prev. Review); leave it blank for
          // the other types so the forecast columns stay aligned.
          if (
            month === csvPrevMonthKey &&
            t.key !== "prev_review" &&
            t.key !== "effective_prediction"
          ) {
            csvRow[`month_${month}`] = "";
          } else {
            csvRow[`month_${month}`] = valueByType[t.key](month);
          }
        });
        data.push(csvRow);
      });
    });

    return { csvData: data, csvHeaders: headers };
  }, [
    activeInventory,
    sellerForecastReviews,
    previousSellerForecastReviews,
    groupedData,
    filteredPredictedUnits,
    pastUnitsSales,
    reviewStatus,
    overrides,
  ]);

  const now = new Date();
  const currentMonthName = now.toLocaleString("en-US", { month: "short" });
  const nextMonthName = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    1
  ).toLocaleString("en-US", { month: "short" });
  const pendingCount = groupedData.filter((row) => {
    const rowKey = `${row.store}-${row.parent_asin}-${row.child_asin}`;
    const predictionData =
      filteredPredictedUnits.find(
        (unit) => (unit.child_asin || unit.asin) === row.child_asin
      ) || getDefaultPredictionData();
    return (
      !reviewStatus[rowKey] &&
      !findCurrentReview(row.child_asin, predictionData)
    );
  }).length;
  const isLoading = activeInventoryLoading || predictedUnitsLoading;
  const bannerPalette = {
    open: { bg: "#fff7e6", border: "#fde68a", color: "#92400e" },
    waiting: { bg: "#eff6ff", border: "#bfdbfe", color: "#1e40af" },
    closed: { bg: "#f1f5f9", border: "#e2e8f0", color: "#475569" },
  }[editWindow.status];
  const rowKeyOf = (row) => `${row.store}-${row.parent_asin}-${row.child_asin}`;
  const allExpanded =
    groupedData.length > 0 &&
    groupedData.every((row) => expandedRows[rowKeyOf(row)]);
  const handleToggleExpandAll = () => {
    if (allExpanded) {
      setExpandedRows({});
    } else {
      const next = {};
      groupedData.forEach((row) => {
        next[rowKeyOf(row)] = true;
      });
      setExpandedRows(next);
    }
  };

  return (
    <>
      {!isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            px: 2,
            py: 1.25,
            borderRadius: "8px",
            fontSize: "13px",
            backgroundColor: bannerPalette.bg,
            border: `1px solid ${bannerPalette.border}`,
            color: bannerPalette.color,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "13px", flex: 1, color: "inherit" }}>
            {editWindow.status === "closed" ? (
              <strong>
                Review period closed. Reopens on {nextMonthName} 1.
              </strong>
            ) : editWindow.status === "waiting" ? (
              <>
                <strong>
                  Waiting for the {currentMonthName} model forecast.
                </strong>{" "}
                Review opens once it is published (the forecast shown was
                published {latestForecastDate}).
              </>
            ) : (
              <>
                <strong>
                  Review due by {currentMonthName} {FORECAST_EDIT_CUTOFF_DAY}.
                </strong>{" "}
                {latestForecastDate &&
                  `New model forecast published ${latestForecastDate}. `}
                Accept the model forecast or save a manual forecast for each
                SKU.
              </>
            )}
          </Typography>
          {groupedData.length > 0 && (
            <Typography
              sx={{ fontSize: "12px", fontWeight: 600, color: "inherit" }}
            >
              {pendingCount} of {groupedData.length} pending
            </Typography>
          )}
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "calc(100vh - 200px)",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          overflow: "auto",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={8}
                sx={{
                  backgroundColor: "#f8fafc",
                  position: "sticky",
                  top: 0,
                  zIndex: 11,
                  padding: "12px 16px",
                  borderBottom: "2px solid #e2e8f0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleToggleExpandAll}
                    disabled={isLoading || groupedData.length === 0}
                    startIcon={
                      allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      borderRadius: "8px",
                      color: "#475569",
                      borderColor: "#cbd5e1",
                      "&:hover": {
                        borderColor: "#94a3b8",
                        backgroundColor: "#f1f5f9",
                      },
                    }}
                  >
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </Button>
                  <CSVLink
                    data={csvData}
                    filename={`${store}_seller_forecast_report.csv`}
                    headers={csvHeaders}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                      fontSize: "13px",
                      fontWeight: 600,
                      padding: "8px 16px",
                      borderRadius: "8px",
                      backgroundColor: "#10b981",
                      color: "#ffffff",
                      transition: "all 0.2s ease-in-out",
                      boxShadow: "0 2px 4px rgba(16, 185, 129, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#059669";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(16, 185, 129, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#10b981";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(16, 185, 129, 0.3)";
                    }}
                  >
                    <DownloadIcon
                      style={{ marginRight: "8px", fontSize: "18px" }}
                      disabled={activeInventoryLoading || predictedUnitsLoading}
                    />
                    <span>Download as CSV</span>
                  </CSVLink>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <StyledHeaderCell sx={{ width: "50px" }} />
              <StyledHeaderCell sx={{ width: "50px" }}>#</StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "120px" }}>Store</StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "130px" }}>
                Parent ASIN
              </StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "130px" }}>
                Child ASIN
              </StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "150px" }}>SKU</StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "180px" }}>
                Sales Forecast Next 7 Months (day to day)
              </StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "120px" }}>
                Status
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(activeInventoryLoading || predictedUnitsLoading) && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                    py={8}
                  >
                    <CircularProgress
                      size={48}
                      thickness={4}
                      sx={{ color: "#3b82f6" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      Loading forecast data...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!activeInventoryLoading &&
              !predictedUnitsLoading &&
              activeInventory &&
              sellerForecastReviews &&
              (() => {
                // Show all rows from groupedData, regardless of whether they have predictions
                const filteredRows = groupedData;
                return filteredRows.map((row, index) => {
                  const rowKey = `${row.store}-${row.parent_asin}-${row.child_asin}`;
                  const isExpanded = expandedRows[rowKey];
                  const isSubmitting = submittingRows[rowKey];

                  // Check if the previous row is expanded
                  const prevRow = index > 0 ? filteredRows[index - 1] : null;
                  const prevRowKey = prevRow
                    ? `${prevRow.store}-${prevRow.parent_asin}-${prevRow.child_asin}`
                    : null;
                  const isPrevExpanded = prevRowKey
                    ? expandedRows[prevRowKey]
                    : false;

                  const foundPrediction = filteredPredictedUnits.find(
                    (unit) => (unit.child_asin || unit.asin) === row.child_asin
                  );
                  const hasPrediction = !!foundPrediction;
                  const predictionData =
                    foundPrediction || getDefaultPredictionData();
                  const currentForecast = predictionData?.forecast_total || 0;
                  const forecastDate = predictionData?.calculated_at || "";
                  const predictions = predictionData?.predictions || {};

                  const lastYearBreakdown = {};
                  // null means no data for the SKU group, rendered as "-"
                  let lastMonthUnits = null;
                  let lastYearLastMonthUnits = null;
                  if (row.originalGroup && pastUnitsSales) {
                    row.originalGroup.forEach((groupRow) => {
                      const pastData = pastUnitsSales.find(
                        (unit) => unit.sku === groupRow.sku
                      );
                      if (pastData?.last_year_units_next_7m_breakdown) {
                        Object.entries(
                          pastData.last_year_units_next_7m_breakdown
                        ).forEach(([month, value]) => {
                          lastYearBreakdown[month] =
                            (lastYearBreakdown[month] || 0) +
                            (Number(value) || 0);
                        });
                      }
                      if (pastData?.last_month_units != null) {
                        lastMonthUnits =
                          (lastMonthUnits || 0) +
                          (Number(pastData.last_month_units) || 0);
                      }
                      if (pastData?.last_year_last_month_units != null) {
                        lastYearLastMonthUnits =
                          (lastYearLastMonthUnits || 0) +
                          (Number(pastData.last_year_last_month_units) || 0);
                      }
                    });
                  }

                  const existingReview = findCurrentReview(
                    row.child_asin,
                    predictionData
                  );

                  const latestReview = findLatestReview(
                    sellerForecastReviews,
                    row.child_asin
                  );
                  const previousReview = findLatestReview(
                    previousSellerForecastReviews,
                    row.child_asin
                  );

                  const statusFinal =
                    reviewStatus[rowKey] ||
                    (existingReview
                      ? existingReview.review === "override"
                        ? "Override"
                        : "accepted"
                      : undefined);

                  // Handle both old and new override structures
                  let overrideDataFinal = overrides[rowKey];

                  if (
                    !overrideDataFinal &&
                    existingReview &&
                    existingReview.review === "override"
                  ) {
                    // Check if using new nested structure
                    if (existingReview.override) {
                      const predictions = {};
                      const override_reason = {};
                      const override_notes = {};

                      // Extract data from override_by_months
                      const monthsData =
                        existingReview.override.override_by_months ||
                        existingReview.override.overrideByMonths;

                      if (monthsData) {
                        Object.entries(monthsData).forEach(([month, data]) => {
                          predictions[month] = data.value;
                          override_reason[month] = data.reason || "";
                          override_notes[month] = data.notes || "";
                        });
                      }

                      overrideDataFinal = {
                        total: existingReview.override.total,
                        predictions,
                        override_reason,
                        override_notes,
                      };
                    } else {
                      // Fall back to old flat structure
                      overrideDataFinal = {
                        total: existingReview.override_total,
                        predictions: existingReview.override_breakdown,
                        override_reason: existingReview.override_reason,
                        override_notes: existingReview.override_notes,
                      };
                    }
                  }

                  return (
                    <ForecastRow
                      key={rowKey}
                      row={row}
                      rowKey={rowKey}
                      index={index}
                      status={statusFinal}
                      overrideData={overrideDataFinal}
                      isExpanded={isExpanded}
                      isPrevExpanded={isPrevExpanded}
                      isSubmitting={isSubmitting}
                      currentForecast={currentForecast}
                      hasPrediction={hasPrediction}
                      noActiveInventory={!!row.noActiveInventory}
                      forecastDate={forecastDate}
                      predictions={predictions}
                      predictionData={predictionData}
                      lastYearBreakdown={lastYearBreakdown}
                      lastMonthUnits={lastMonthUnits}
                      lastYearLastMonthUnits={lastYearLastMonthUnits}
                      latestReview={latestReview}
                      previousReview={previousReview}
                      userAllowedToRespond={userAllowedToRespond}
                      editWindow={editWindow}
                      handleToggleExpand={handleToggleExpand}
                      handleSubmitReview={handleSubmitReview}
                    />
                  );
                });
              })()}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SellerForecast;
